import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { isEmailDomainAllowed } from '../lib/auth-domain';
import { createClient, isSupabaseConfigured } from '../lib/supabase';

const AuthContext = createContext(undefined);

export function AuthProvider({ children }) {
  const userIdRef = useRef(undefined);
  const [user, setUserRaw] = useState(null);
  const setUser = useCallback((u) => {
    userIdRef.current = u?.id;
    setUserRaw(u);
  }, []);
  const [loading, setLoading] = useState(true);
  const configError = !isSupabaseConfigured();

  const supabase = useMemo(
    () => (configError ? null : createClient()),
    [configError],
  );

  const resolveAppUser = useCallback(
    async (supaUser) => {
      if (!supaUser || !supabase) return null;
      if (!isEmailDomainAllowed(supaUser.email)) {
        await supabase.auth.signOut();
        return null;
      }
      const displayName =
        supaUser.user_metadata?.full_name ??
        supaUser.user_metadata?.name ??
        supaUser.email?.split('@')[0];

      return {
        ...supaUser,
        displayName,
      };
    },
    [supabase],
  );

  const loadAuthUser = useCallback(async () => {
    if (!supabase) return null;
    try {
      const {
        data: { user: supaUser },
      } = await supabase.auth.getUser();
      return await resolveAppUser(supaUser);
    } catch {
      return null;
    }
  }, [supabase, resolveAppUser]);

  useEffect(() => {
    if (configError) {
      setLoading(false);
      return undefined;
    }

    let isMounted = true;

    async function init() {
      try {
        const appUser = await loadAuthUser();
        if (isMounted) setUser(appUser);
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    void init();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (
        event === 'TOKEN_REFRESHED' &&
        session?.user?.id === userIdRef.current
      ) {
        return;
      }
      const appUser = await resolveAppUser(session?.user ?? null);
      if (isMounted) {
        setUser(appUser);
        setLoading(false);
      }
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, [configError, loadAuthUser, resolveAppUser, setUser, supabase]);

  const signOut = useCallback(async () => {
    if (!supabase) return;
    await supabase.auth.signOut();
    setUser(null);
  }, [supabase, setUser]);

  const value = useMemo(
    () => ({
      user,
      loading,
      signOut,
      configError,
    }),
    [user, loading, signOut, configError],
  );

  return (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}
